/**
 * SiteFloorTree
 *
 * Searchable tree sidebar listing all sites and their floor plans.
 * Selecting a floor plan updates the shared LocationContext (and URL params).
 *
 * Layout:
 *  ┌─────────────────────────────────┐
 *  │ 🔍 Search floors...              │
 *  ├─────────────────────────────────┤
 *  │ ▼ building-a                    │
 *  │    ● Floor 1  [office] [HQ]     │  ← selected
 *  │    ○ Floor 2  [server-room]     │
 *  │ ▶ warehouse-1  (3 floors)       │
 *  └─────────────────────────────────┘
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Badge,
} from '@mui/material';
import { AppIconButton, AppTextField } from '../design-system';
import {
  Search as SearchIcon,
  ExpandMore,
  ChevronRight,
  Layers as LayersIcon,
  Circle as CircleIcon,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import { useLocationContext } from '../../contexts/LocationContext';
import type { FloorPlanConfigWithUrl } from '@cnapse/shared/schemas.js';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns true if the floor plan or its tags match the query */
function floorMatchesSearch(floor: FloorPlanConfigWithUrl, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (floor.displayName.toLowerCase().includes(q)) return true;
  return (floor.tags ?? []).some(t => t.toLowerCase().includes(q));
}

/** Returns true if any floor in a site matches or the siteId matches */
function siteMatchesSearch(
  site: { siteId: string; displayName: string | null; siteAddressLabel: string | null },
  floors: FloorPlanConfigWithUrl[],
  query: string,
): boolean {
  if (!query) return true;
  const loweredQuery = query.toLowerCase();
  if (site.siteId.toLowerCase().includes(loweredQuery)) return true;
  if ((site.displayName ?? '').toLowerCase().includes(loweredQuery)) return true;
  if ((site.siteAddressLabel ?? '').toLowerCase().includes(loweredQuery)) return true;
  return floors.some(f => floorMatchesSearch(f, query));
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  /** When true the component renders as a plain content block (no fixed-width
   *  sidebar shell, no collapse toggle) — used inside a popover. */
  popoverMode?: boolean;
  /** Called after the user selects a floor — lets the parent close a popover. */
  onFloorSelected?: () => void;
}

const SiteFloorTree: React.FC<Props> = ({ popoverMode = false, onFloorSelected }) => {
  const {
    sites,
    sitesLoading,
    sitesError,
    floorPlans,
    floorPlansLoading,
    selectedSiteId,
    selectedFloorId,
    setSelection,
  } = useLocationContext();

  const [search, setSearch] = useState('');
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (popoverMode || typeof window === 'undefined') {
      return false;
    }
    return window.localStorage.getItem('location-site-floor-tree-collapsed') === 'true';
  });

  useEffect(() => {
    if (popoverMode || typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem('location-site-floor-tree-collapsed', collapsed ? 'true' : 'false');
  }, [collapsed, popoverMode]);

  // Effective search: combine free text + active tag filter
  const effectiveSearch = activeTagFilter ?? search;

  // Expand the selected site automatically
  const allExpandedSites = useMemo(() => {
    const s = new Set(expandedSites);
    if (selectedSiteId) s.add(selectedSiteId);
    // Auto-expand when only one site exists
    if (sites.length === 1) s.add(sites[0].siteId);
    return s;
  }, [expandedSites, selectedSiteId, sites]);

  function toggleSite(siteId: string) {
    setExpandedSites(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  function handleSelectFloor(siteId: string, floorId: string) {
    setSelection(siteId, floorId);
    onFloorSelected?.();
  }

  function handleTagChipClick(tag: string) {
    setActiveTagFilter(prev => (prev === tag ? null : tag));
    setSearch('');
  }

  const selectedSite = useMemo(
    () => sites.find(site => site.siteId === selectedSiteId) ?? null,
    [selectedSiteId, sites],
  );

  // Floors displayed for the selected site — used by children items
  // We also need floors per-site for search filtering, but we only have floors
  // for the currently selected site loaded in context. For other sites we
  // fall back to showing the site row with floor count only (no per-floor search).
  const floorsForSite = (siteId: string): FloorPlanConfigWithUrl[] =>
    siteId === selectedSiteId ? floorPlans : [];

  const visibleSites = useMemo(
    () =>
      sites.filter(s =>
        siteMatchesSearch(s, floorsForSite(s.siteId), effectiveSearch),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, selectedSiteId, floorPlans, effectiveSearch],
  );

  if (!popoverMode && collapsed) {
    return (
      <Box
        sx={{
          width: 48,
          minWidth: 48,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 1,
          gap: 1,
        }}
      >
        <Tooltip title="Expand Sites & Floors">
          <AppIconButton size="small" onClick={() => setCollapsed(false)}>
            <KeyboardDoubleArrowRight fontSize="small" />
          </AppIconButton>
        </Tooltip>
        <Tooltip title={selectedSiteId ? `Selected site: ${selectedSite?.displayName ?? selectedSiteId}` : 'Sites & Floors'}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28 }}>
            <LayersIcon fontSize="small" color={selectedSiteId ? 'primary' : 'disabled'} />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={popoverMode ? {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      } : {
        width: 260,
        minWidth: 260,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        {!popoverMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, flex: 1 }}>
              Sites &amp; Floors
            </Typography>
            <Tooltip title="Collapse Sites & Floors">
              <AppIconButton size="small" onClick={() => setCollapsed(true)}>
                <KeyboardDoubleArrowLeft fontSize="small" />
              </AppIconButton>
            </Tooltip>
          </Box>
        )}
        <AppTextField
          size="small"
          fullWidth
          placeholder="Search sites or floors ..."
          value={activeTagFilter ?? search}
          onChange={e => {
            setActiveTagFilter(null);
            setSearch(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {activeTagFilter && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              label={`Tag: ${activeTagFilter}`}
              size="small"
              color="primary"
              onDelete={() => setActiveTagFilter(null)}
            />
          </Box>
        )}
      </Box>

      <Divider />

      {/* Tree body */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {sitesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {sitesError && (
          <Alert severity="error" sx={{ m: 1, fontSize: '0.75rem' }}>
            Failed to load sites
          </Alert>
        )}
        {!sitesLoading && !sitesError && visibleSites.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {effectiveSearch ? 'No matches found.' : 'No sites configured.'}
          </Typography>
        )}

        <List dense disablePadding>
          {visibleSites.map(site => {
            const isExpanded = allExpandedSites.has(site.siteId);
            const isSelectedSite = site.siteId === selectedSiteId;
            const floors = floorsForSite(site.siteId);
            const visibleFloors = floors.filter(f =>
              floorMatchesSearch(f, effectiveSearch),
            );

            return (
              <React.Fragment key={site.siteId}>
                {/* Site row */}
                <ListItemButton
                  onClick={() => {
                    toggleSite(site.siteId);
                    // If selecting a new site, load its floors by calling setSelection
                    if (!isSelectedSite) {
                      setSelection(site.siteId, null);
                    }
                  }}
                  selected={isSelectedSite && !selectedFloorId}
                  sx={{ py: 0.75, pl: 1 }}
                >
                  {isExpanded ? (
                    <ExpandMore fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  ) : (
                    <ChevronRight fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  )}
                  <LayersIcon fontSize="small" sx={{ mr: 1, color: isSelectedSite ? 'primary.main' : 'text.secondary' }} />
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={isSelectedSite ? 600 : 400} noWrap>
                        {site.displayName ?? site.siteAddressLabel ?? site.siteId}
                      </Typography>
                    }
                    secondary={
                      (site.displayName || site.siteAddressLabel) && (site.displayName !== site.siteId)
                        ? site.siteId
                        : undefined
                    }
                  />
                  {/* Tag count badge */}
                  {site.tagCounts.total > 0 && (
                    <Tooltip title={`${site.tagCounts.online} online, ${site.tagCounts.offline} offline${site.tagCounts.sos > 0 ? `, ${site.tagCounts.sos} SOS` : ''}`}>
                      <Badge
                        badgeContent={site.tagCounts.total}
                        color={site.tagCounts.sos > 0 ? 'error' : 'default'}
                        max={999}
                        sx={{ mr: 1 }}
                      />
                    </Tooltip>
                  )}
                  {!isExpanded && (
                    <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                      {site.floorCount}F
                    </Typography>
                  )}
                </ListItemButton>

                {/* Floor plan rows */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {isSelectedSite && floorPlansLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={16} />
                    </Box>
                  )}
                  {isSelectedSite && !floorPlansLoading && visibleFloors.length === 0 && floors.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 5, pb: 0.5, display: 'block' }}>
                      No matches
                    </Typography>
                  )}
                  {isSelectedSite && !floorPlansLoading && floors.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 5, pb: 0.5, display: 'block' }}>
                      No floor plans
                    </Typography>
                  )}
                  {(isSelectedSite ? visibleFloors : []).map(floor => {
                    const isSelected = floor.floorId === selectedFloorId;
                    return (
                      <ListItemButton
                        key={floor.floorId}
                        selected={isSelected}
                        onClick={() => handleSelectFloor(site.siteId, floor.floorId)}
                        sx={{ pl: 5, py: 0.5 }}
                      >
                        <CircleIcon
                          sx={{
                            fontSize: 8,
                            mr: 1,
                            color: isSelected ? 'primary.main' : 'text.disabled',
                          }}
                        />
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 600 : 400 }}>
                              {floor.displayName}
                            </Typography>
                          }
                          secondary={
                            (floor.tags ?? []).length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                                {(floor.tags ?? []).map(tag => (
                                  <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant={activeTagFilter === tag ? 'filled' : 'outlined'}
                                    color={activeTagFilter === tag ? 'primary' : 'default'}
                                    sx={{ height: 16, fontSize: '0.65rem', cursor: 'pointer' }}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleTagChipClick(tag);
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : null
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                  {/* For non-selected sites, just show floor count placeholder */}
                  {!isSelectedSite && (
                    <Box sx={{ pl: 5, py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {site.floorCount} floor plan{site.floorCount !== 1 ? 's' : ''}
                        {' — '}select site to browse
                      </Typography>
                    </Box>
                  )}
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default SiteFloorTree;
